export const DEFAULT_DEMONSTRATED_LOAD_SETTINGS = {
    serviceLimitAmps: 100,
    serviceVoltage: 240,
    diversityFactor: 1.25,
    conductorAmpacityAmps: null,
    analysisMonths: 12,
};

export const MAX_PROPOSED_LOADS = 5;

export const PROPOSED_LOAD_PRESETS = [
    { label: "EVSE (Level 2)", amps: 32 },
    { label: "Hot tub", amps: 40 },
    { label: "Pool heat pump", amps: 50 },
    { label: "Secondary suite", amps: 60 },
    { label: "Induction range", amps: 40 },
    { label: "ASHP — Small (<15 kBTU/h)", amps: 15 },
    { label: "ASHP — Medium (25–30 kBTU/h)", amps: 30 },
    { label: "ASHP — Large (>40 kBTU/h)", amps: 40 },
];

export const createProposedLoadKey = (index = 0) => `${Date.now()}_${index}`;

const parseHourStart = (start) => {
    if (typeof start === "number") return new Date(start < 1e12 ? start * 1000 : start);
    if (typeof start === "object" && start?._seconds) return new Date(start._seconds * 1000);
    const date = new Date(start);
    return isNaN(date.getTime()) ? null : date;
};

const kwhToAmps = (kwh, voltage) => (Number(kwh) * 1000) / Number(voltage);

/**
 * Compute demonstrated load per ESA Bulletin 8-3-15 Q4.
 * Diversity applies only to the single peak hour.
 */
export const computeDemonstratedLoad = (hourlyData, settings, proposedLoads = {}) => {
    const mergedSettings = { ...DEFAULT_DEMONSTRATED_LOAD_SETTINGS, ...settings };
    const {
        serviceLimitAmps,
        serviceVoltage,
        diversityFactor,
        conductorAmpacityAmps,
        analysisMonths,
    } = mergedSettings;

    const warnings = [];

    if (!Array.isArray(hourlyData) || hourlyData.length === 0) {
        return { warnings: ["No hourly utility data available."] };
    }

    const validHours = hourlyData
        .map((row, index) => {
            const kwh = Number(row?.value);
            const start = parseHourStart(row?.start);
            if (!Number.isFinite(kwh) || !start) return null;
            return { kwh, start, index, rawStart: row.start };
        })
        .filter(Boolean);

    if (validHours.length === 0) {
        return { warnings: ["No valid hourly kWh readings found."] };
    }

    validHours.sort((a, b) => a.start - b.start);

    const latestStart = validHours[validHours.length - 1].start;
    const analysisCutoff = new Date(latestStart);
    analysisCutoff.setMonth(analysisCutoff.getMonth() - Number(analysisMonths));

    const analysisHours = validHours.filter((h) => h.start >= analysisCutoff);
    const hoursUsed = analysisHours.length > 0 ? analysisHours : validHours;

    const periodStart = hoursUsed[0].start.toISOString();
    const periodEnd = hoursUsed[hoursUsed.length - 1].start.toISOString();

    const spanMs = hoursUsed[hoursUsed.length - 1].start - hoursUsed[0].start;
    const spanMonths = spanMs / (1000 * 60 * 60 * 24 * 30.4375);
    if (spanMonths < 12) {
        warnings.push(
            `Analysis period spans ${spanMonths.toFixed(1)} months (${hoursUsed.length} hours). A full 12 months is recommended for demonstrated load calculations.`
        );
    }

    let peakHour = hoursUsed[0];
    let secondPeakKwh = 0;

    for (const hour of hoursUsed) {
        if (hour.kwh > peakHour.kwh) {
            secondPeakKwh = peakHour.kwh;
            peakHour = hour;
        } else if (hour.kwh > secondPeakKwh && hour !== peakHour) {
            secondPeakKwh = hour.kwh;
        }
    }

    if (secondPeakKwh > 0 && peakHour.kwh >= 2 * secondPeakKwh) {
        warnings.push(
            `Peak hour (${peakHour.kwh.toFixed(2)} kWh) is more than double the next-highest hour (${secondPeakKwh.toFixed(2)} kWh), which may indicate an outlier.`
        );
    }

    const peakDemandAmps = kwhToAmps(peakHour.kwh * diversityFactor, serviceVoltage);
    const peakHourAmpsUndiversified = kwhToAmps(peakHour.kwh, serviceVoltage);

    const effectiveServiceLimitAmps =
        conductorAmpacityAmps != null && Number(conductorAmpacityAmps) > 0
            ? Math.min(Number(serviceLimitAmps), Number(conductorAmpacityAmps))
            : Number(serviceLimitAmps);

    const totalProposedAmps = Object.values(proposedLoads || {}).reduce(
        (sum, load) => sum + (Number(load?.amps) || 0),
        0
    );

    const headroomAmps = effectiveServiceLimitAmps - peakDemandAmps - totalProposedAmps;

    if (headroomAmps < 0) {
        warnings.push(
            `Demonstrated peak demand (${peakDemandAmps.toFixed(1)} A) plus proposed loads (${totalProposedAmps.toFixed(1)} A) exceeds the effective service limit (${effectiveServiceLimitAmps.toFixed(0)} A).`
        );
    }

    return {
        peakHourKwh: peakHour.kwh,
        peakHourStart: peakHour.start.toISOString(),
        peakHourIndex: peakHour.index,
        secondPeakKwh,
        peakDemandAmps,
        peakHourAmpsUndiversified,
        effectiveServiceLimitAmps,
        serviceLimitAmps: Number(serviceLimitAmps),
        totalProposedAmps,
        headroomAmps,
        diversityFactor: Number(diversityFactor),
        serviceVoltage: Number(serviceVoltage),
        analysisMonths: Number(analysisMonths),
        hoursAnalyzed: hoursUsed.length,
        periodStart,
        periodEnd,
        warnings,
    };
};
